import { ImageMediaEntity, VideoMediaEntity } from '@/pages/AdvancedEditor/components/panels/media/types';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { logger } from '@/services/logger';
import { observer } from 'mobx-react-lite';
import { GifTile } from './GifTile';
import { ImageTile } from './ImageTile';
import { VideoTile } from './VideoTile';

const MEDIA_SCALE = 0.5;

const calculateCenter = (elementWidth: number, elementHeight: number, containerWidth: number, containerHeight: number) => {
    const x = (containerWidth - elementWidth) / 2;
    const y = (containerHeight - elementHeight) / 2;

    return { x, y };
};

const calculateSize = (width: number, height: number, containerWidth: number, containerHeight: number) => {
    const scaledContainerWidth = containerWidth * MEDIA_SCALE;
    const scaledContainerHeight = containerHeight * MEDIA_SCALE;

    const scale = Math.min(scaledContainerWidth / width, scaledContainerHeight / height);
    return {
        width: width * scale,
        height: height * scale,
    };
};

interface Props {
    entity: VideoMediaEntity | ImageMediaEntity;
    width: number;
    height: number;
    onDelete: (id: string) => Promise<void>;
}

export const MediaTile = observer(({ entity, width, height, onDelete }: Props) => {
    const { store } = useAdvancedEditorContext();

    const handleDelete = async () => {
        await onDelete(entity.id);
    };

    const handleVideoClick = () => {
        const { width, height } = calculateSize(entity.width, entity.height, store.width, store.height);

        store.activePage.addElement({
            type: 'video',
            src: entity.src,
            width,
            height,
            duration: (entity as VideoMediaEntity).duration,
            name: entity.filename,
            volume: 1,
            custom: {
                mimetype: entity.mimetype,
                originalWidth: entity.width,
                originalHeight: entity.height,
            },
            ...calculateCenter(width, height, store.width, store.height),
        });
    };

    const handleImageClick = async () => {
        const { width, height } = calculateSize(entity.width, entity.height, store.width, store.height);
        let src;
        const isSvg = entity.src.endsWith('.svg');

        if (isSvg) {
            try {
                const response = await fetch(entity.src);
                if (!response.ok) {
                    throw new Error(`Failed to fetch SVG: ${entity.src}`);
                }
                const svgText = await response.text();

                const encodedSvg = encodeURIComponent(svgText).replace(/'/g, '%27').replace(/"/g, '%22');

                src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
            } catch (error) {
                logger.error(error);
            }
        }
        store.activePage.addElement({
            type: isSvg ? 'svg' : 'image',
            src: src || entity.src,
            width,
            height,
            name: entity.filename,
            custom: {
                mimetype: entity.mimetype,
                originalWidth: entity.width,
                originalHeight: entity.height,
            },
            ...calculateCenter(width, height, store.width, store.height),
        });
    };

    if (entity.mimetype.includes('video')) {
        return (
            <VideoTile
                entity={entity as VideoMediaEntity}
                width={width}
                height={height}
                onDelete={handleDelete}
                onClick={handleVideoClick}
            />
        );
    }

    if (entity.mimetype.includes('image/gif')) {
        return <GifTile entity={entity} onDelete={handleDelete} width={width} height={height} onClick={handleImageClick} />;
    }

    if (entity.mimetype.includes('image')) {
        return <ImageTile entity={entity} onDelete={handleDelete} width={width} height={height} onClick={handleImageClick} />;
    }

    return null;
});
